import { forwardRef, HTMLAttributes, useEffect, useRef, useState } from 'react'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPTaxonomy } from '@/types'

export type FiltersProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    terms: WPTaxonomy[]
    fetching: boolean
    // eslint-disable-next-line no-unused-vars
    onFilterChange: (term: WPTaxonomy) => void
  }>

const FilterList = forwardRef<HTMLDivElement, FiltersProps>(
  ({ terms, fetching, onFilterChange, ...props }, ref) => {
    const [currentFilter, setCurrentFilter] = useState('')
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { strings } = useStore()

    const handleFilterChange = (termId: string) => {
      setCurrentFilter(termId)

      if (onFilterChange) {
        if (termId === '') {
          onFilterChange({} as WPTaxonomy)
        } else {
          const selectedTerm = terms?.find(
            (term) => term.term_id.toString() === termId
          )
          if (selectedTerm) {
            onFilterChange(selectedTerm)
          }
        }
      }
    }

    const handleMobileButtonClick = () => {
      setIsDropdownOpen(!isDropdownOpen)
    }

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    return (
      <section ref={ref} {...props}>
        <Container>
          {/* Desktop view */}
          <div className="hidden flex-wrap items-center gap-spacing-2xs md:flex">
            <Button
              isLiquid
              variant={currentFilter === '' ? 'accent' : 'secondary'}
              isActive={currentFilter === ''}
              className="[&_svg[data-background]]:stroke-border-menu-light"
              onClick={() => handleFilterChange('')}
            >
              <TextEffect>
                <Typography variant="regular">
                  {strings?.labels?.all}
                </Typography>
              </TextEffect>
            </Button>
            {terms?.map((term, index) => (
              <Button
                key={index}
                isLiquid
                variant={
                  currentFilter === term.term_id.toString()
                    ? 'accent'
                    : 'secondary'
                }
                isActive={currentFilter === term.term_id.toString()}
                className="[&_svg[data-background]]:stroke-border-menu-light"
                onClick={() => handleFilterChange(term.term_id.toString())}
              >
                <TextEffect>
                  <Typography variant="regular">{term.name}</Typography>
                </TextEffect>
              </Button>
            ))}
            {fetching ? (
              <Spinner className="ml-spacing-2xs" variant="medium" />
            ) : null}
          </div>
          {/* Mobile view with dropdown */}
          <div ref={dropdownRef} className="relative z-20 md:hidden">
            <Button
              isLiquid
              full
              variant="accent"
              className="dark min-h-[4.8rem] w-full justify-between"
              onClick={handleMobileButtonClick}
              endIcon={
                fetching ? (
                  <Spinner variant="small" />
                ) : (
                  <SVGIcon
                    symbol="chevron-down-icon"
                    className={cn([
                      'size-[1rem] transform fill-white-light transition-transform',
                      { 'rotate-180': isDropdownOpen }
                    ])}
                  />
                )
              }
            >
              <Typography variant="regular">
                {currentFilter === ''
                  ? strings?.labels?.all
                  : terms?.find(
                      (term) => term.term_id.toString() === currentFilter
                    )?.name || strings?.labels?.all}
              </Typography>
            </Button>
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-radius-minimal bg-surface-primay-invert-light shadow-lg">
                <div>
                  <button
                    className={cn([
                      'block w-full px-4 py-2 text-left',
                      {
                        'bg-text-primary-dark': currentFilter === ''
                      }
                    ])}
                    onClick={() => {
                      handleFilterChange('')
                      setIsDropdownOpen(false)
                    }}
                  >
                    <Typography variant="regular">
                      {strings?.labels?.all}
                    </Typography>
                  </button>
                  {terms?.map((term) => (
                    <button
                      key={term.term_id}
                      className={cn([
                        'block w-full px-4 py-2 text-left',
                        {
                          'bg-text-primary-dark':
                            currentFilter === term.term_id.toString()
                        }
                      ])}
                      onClick={() => {
                        handleFilterChange(term.term_id.toString())
                        setIsDropdownOpen(false)
                      }}
                    >
                      <Typography variant="regular">{term.name}</Typography>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Container>
      </section>
    )
  }
)

export default FilterList
